// 流量测试:在浏览器里按生成的 rules 顺序逐条模拟 mihomo 的匹配过程
import { ALL_ITEMS, type RuleSetRef } from '../shared/catalog.ts';
import type { UserConfig } from '../shared/config.ts';
import { generateConfig, providerName } from '../shared/generate.ts';

const LIST_BASES: Record<string, string> = {
  fastly: 'https://fastly.jsdelivr.net/gh/MetaCubeX/meta-rules-dat@meta/geo',
  testingcf: 'https://testingcf.jsdelivr.net/gh/MetaCubeX/meta-rules-dat@meta/geo',
  gcore: 'https://gcore.jsdelivr.net/gh/MetaCubeX/meta-rules-dat@meta/geo',
  jsdelivr: 'https://cdn.jsdelivr.net/gh/MetaCubeX/meta-rules-dat@meta/geo',
};
// 与生成配置里的国内 nameserver 一致,优先阿里;失败依次换备用(都支持 JSON + CORS)
const DOH_ENDPOINTS = ['https://dns.alidns.com/resolve', 'https://dns.google/resolve', 'https://cloudflare-dns.com/dns-query'];

const REFS = new Map<string, RuleSetRef>();
for (const item of ALL_ITEMS) for (const ref of item.sets) REFS.set(providerName(ref), ref);
REFS.set(providerName({ type: 'geosite', name: 'geolocation-!cn' }), { type: 'geosite', name: 'geolocation-!cn' });

// ---------- 规则文本解析与匹配 ----------

interface DomainSet {
  kind: 'domain';
  exact: Set<string>;
  /** +.example.com:自身及子域名 */
  suffix: Set<string>;
  /** .example.com:仅子域名 */
  sub: Set<string>;
  wild: { re: RegExp; raw: string }[];
}

interface CidrSet {
  kind: 'ip';
  v4: { net: number; mask: number; raw: string }[];
  v6: { net: bigint; mask: bigint; raw: string }[];
}

export function parseDomains(text: string): DomainSet {
  const set: DomainSet = { kind: 'domain', exact: new Set(), suffix: new Set(), sub: new Set(), wild: [] };
  for (const raw of text.split('\n')) {
    const line = raw.trim().toLowerCase();
    if (!line || line.startsWith('#')) continue;
    if (line.startsWith('+.')) set.suffix.add(line.slice(2));
    else if (line.includes('*')) {
      const src = line.split('.').map((p) => (p === '*' ? '[^.]+' : p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))).join('\\.');
      set.wild.push({ re: new RegExp(`^${src}$`), raw: line });
    } else if (line.startsWith('.')) set.sub.add(line.slice(1));
    else set.exact.add(line);
  }
  return set;
}

export function matchDomain(set: DomainSet, host: string): string | undefined {
  if (set.exact.has(host)) return host;
  const labels = host.split('.');
  for (let k = 0; k < labels.length; k++) {
    const s = labels.slice(k).join('.');
    if (set.suffix.has(s)) return `+.${s}`;
    if (k > 0 && set.sub.has(s)) return `.${s}`;
  }
  return set.wild.find((w) => w.re.test(host))?.raw;
}

function ipv4ToInt(ip: string): number | undefined {
  const parts = ip.split('.');
  if (parts.length !== 4) return undefined;
  let n = 0;
  for (const p of parts) {
    if (!/^\d{1,3}$/.test(p) || Number(p) > 255) return undefined;
    n = n * 256 + Number(p);
  }
  return n;
}

function ipv6ToBig(ip: string): bigint | undefined {
  let s = ip.toLowerCase();
  if (s.includes('.')) {
    // ::ffff:1.2.3.4 形式
    const i = s.lastIndexOf(':');
    const v4 = ipv4ToInt(s.slice(i + 1));
    if (v4 === undefined) return undefined;
    s = `${s.slice(0, i)}:${((v4 >>> 16) & 0xffff).toString(16)}:${(v4 & 0xffff).toString(16)}`;
  }
  const [head, tail, extra] = s.split('::');
  if (extra !== undefined) return undefined;
  const h = head ? head.split(':') : [];
  const t = tail !== undefined ? (tail ? tail.split(':') : []) : [];
  const fill = tail !== undefined ? 8 - h.length - t.length : 0;
  const groups = [...h, ...Array(Math.max(fill, 0)).fill('0'), ...t];
  if (groups.length !== 8 || groups.some((g) => !/^[0-9a-f]{1,4}$/.test(g))) return undefined;
  return groups.reduce((acc, g) => (acc << 16n) + BigInt(parseInt(g, 16)), 0n);
}

export function parseCidrs(text: string): CidrSet {
  const set: CidrSet = { kind: 'ip', v4: [], v6: [] };
  for (const raw of text.split('\n')) {
    const line = raw.trim();
    if (!line || line.startsWith('#')) continue;
    const [addr, bitsRaw] = line.split('/');
    const bits = Number(bitsRaw);
    if (addr.includes(':')) {
      const n = ipv6ToBig(addr);
      if (n === undefined) continue;
      const b = Number.isFinite(bits) ? bits : 128;
      const mask = b === 0 ? 0n : ((1n << 128n) - 1n) ^ ((1n << BigInt(128 - b)) - 1n);
      set.v6.push({ net: n & mask, mask, raw: line });
    } else {
      const n = ipv4ToInt(addr);
      if (n === undefined) continue;
      const b = Number.isFinite(bits) ? bits : 32;
      const mask = b === 0 ? 0 : (0xffffffff << (32 - b)) >>> 0;
      set.v4.push({ net: (n & mask) >>> 0, mask, raw: line });
    }
  }
  return set;
}

export function matchIp(set: CidrSet, ip: string): string | undefined {
  if (ip.includes(':')) {
    const n = ipv6ToBig(ip);
    return n === undefined ? undefined : set.v6.find((c) => (n & c.mask) === c.net)?.raw;
  }
  const n = ipv4ToInt(ip);
  return n === undefined ? undefined : set.v4.find((c) => ((n & c.mask) >>> 0) === c.net)?.raw;
}

const listCache = new Map<string, Promise<DomainSet | CidrSet>>();

function loadList(ref: RuleSetRef, mirror: string): Promise<DomainSet | CidrSet> {
  const base = LIST_BASES[mirror] ?? LIST_BASES.testingcf;
  const url = `${base}/${ref.type}/${encodeURIComponent(ref.name).replace(/!/g, '%21')}.list`;
  let p = listCache.get(url);
  if (!p) {
    p = fetch(url).then(async (res) => {
      if (!res.ok) throw new Error(`下载规则 ${ref.name} 失败 (HTTP ${res.status})`);
      const text = await res.text();
      return ref.type === 'geosite' ? parseDomains(text) : parseCidrs(text);
    });
    p.catch(() => listCache.delete(url));
    listCache.set(url, p);
  }
  return p;
}

async function askDoh(endpoint: string, host: string, type: 'A' | 'AAAA'): Promise<string[]> {
  const q = new URLSearchParams({ name: host, type });
  const res = await fetch(`${endpoint}?${q}`, { headers: { accept: 'application/dns-json' }, signal: AbortSignal.timeout(5000) });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = (await res.json()) as { Answer?: { type: number; data: string }[] };
  return (data.Answer ?? []).filter((a) => a.type === 1 || a.type === 28).map((a) => a.data);
}

/** 返回 undefined 表示所有 DoH 都不可用 */
async function resolve(host: string): Promise<string[] | undefined> {
  for (const endpoint of DOH_ENDPOINTS) {
    try {
      const [a, aaaa] = await Promise.all([askDoh(endpoint, host, 'A'), askDoh(endpoint, host, 'AAAA')]);
      return [...a, ...aaaa];
    } catch {
      /* 换下一个 */
    }
  }
  return undefined;
}

// ---------- 输入解析 ----------

export type Target = { host: string; isIp: false } | { host: string; isIp: true };

const HOST_RE = /^(?=.{1,253}$)(?:[a-z0-9_](?:[a-z0-9_-]{0,61}[a-z0-9])?\.)*[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

export function parseTarget(input: string): Target {
  const s = input.trim();
  if (!s) throw new Error('请输入网址、域名或 IP');
  let host: string;
  try {
    host = new URL(s.includes('://') ? s : `http://${s}`).hostname.toLowerCase();
  } catch {
    throw new Error('无法识别的网址');
  }
  host = host.replace(/^\[|\]$/g, '').replace(/\.$/, '');
  if (ipv4ToInt(host) !== undefined || (host.includes(':') && ipv6ToBig(host) !== undefined)) return { host, isIp: true };
  if (!HOST_RE.test(host)) throw new Error('无法识别的域名');
  return { host, isIp: false };
}

// ---------- 模拟 ----------

export interface TraceRow {
  index: number;
  rule: string;
  status: 'miss' | 'hit' | 'skip-no-resolve' | 'skip-type';
  matched?: string;
}

export interface TestResult {
  target: Target;
  resolved?: string[];
  /** DNS 解析失败(所有 DoH 都不可用),后续 IP 规则按不匹配处理 */
  resolveFailed?: boolean;
  rows: TraceRow[];
  hit?: TraceRow & { target: string };
  /** 策略组默认选择链:例如 📺 Netflix → 🇭🇰 香港 → (测速) */
  chain: string[];
  chainNote?: string;
}

interface Group {
  name: string;
  type: string;
  proxies?: string[];
  use?: string[];
}

function followDefaults(start: string, groups: Map<string, Group>): { chain: string[]; note?: string } {
  const chain = [start];
  let cur = groups.get(start);
  const seen = new Set([start]);
  while (cur) {
    if (cur.type === 'url-test') return { chain, note: '组内自动测速选择节点' };
    const first = cur.proxies?.[0];
    if (!first) return { chain, note: cur.use ? '组内为机场节点(由客户端拉取)' : undefined };
    chain.push(first);
    if (seen.has(first)) break;
    seen.add(first);
    cur = groups.get(first);
  }
  const last = chain[chain.length - 1];
  if (last === 'DIRECT') return { chain, note: '直连' };
  if (last === 'REJECT') return { chain, note: '拦截' };
  return { chain };
}

export async function runTest(input: string, cfg: UserConfig, origin: string, onProgress?: (msg: string) => void): Promise<TestResult> {
  const target = parseTarget(input);
  const draft: UserConfig = {
    ...cfg,
    upstreams: cfg.upstreams.map((u, i) => ({ name: u.name || `机场${i + 1}`, url: u.url || `https://example.com/${i}` })),
  };
  const out = generateConfig(draft, draft.upstreams.map(() => ({ kind: 'provider' })), { origin });
  const rules = out.rules as string[];
  const groups = new Map((out['proxy-groups'] as Group[]).map((g) => [g.name, g]));

  const result: TestResult = { target, rows: [], chain: [] };
  let ips: string[] | undefined = target.isIp ? [target.host] : undefined;

  for (let i = 0; i < rules.length; i++) {
    const parts = rules[i].split(',');
    const [type] = parts;
    const row: TraceRow = { index: i + 1, rule: rules[i], status: 'miss' };
    let hit: string | undefined;

    if (type === 'MATCH') {
      hit = '未命中任何规则';
    } else if (type === 'DOMAIN-SUFFIX') {
      const d = parts[1];
      if (target.isIp) row.status = 'skip-type';
      else if (target.host === d || target.host.endsWith(`.${d}`)) hit = d;
    } else if (type === 'RULE-SET') {
      const ref = REFS.get(parts[1]);
      if (ref?.type === 'geosite') {
        if (target.isIp) row.status = 'skip-type';
        else {
          onProgress?.(`匹配第 ${i + 1} 条:${ref.name}`);
          hit = matchDomain((await loadList(ref, cfg.mirror)) as DomainSet, target.host);
        }
      } else if (ref) {
        const noResolve = parts[3] === 'no-resolve';
        if (!ips && noResolve) row.status = 'skip-no-resolve';
        else {
          if (!ips) {
            onProgress?.(`解析 ${target.host}`);
            const got = await resolve(target.host);
            result.resolveFailed = got === undefined;
            ips = got ?? [];
            result.resolved = ips;
          }
          onProgress?.(`匹配第 ${i + 1} 条:${ref.name}`);
          const set = (await loadList(ref, cfg.mirror)) as CidrSet;
          for (const ip of ips) {
            const m = matchIp(set, ip);
            if (m) {
              hit = `${ip} ∈ ${m}`;
              break;
            }
          }
        }
      }
    }

    if (hit !== undefined) {
      row.status = 'hit';
      row.matched = hit;
      result.rows.push(row);
      const policy = parts[0] === 'MATCH' ? parts[1] : parts[2];
      result.hit = { ...row, target: policy };
      const f = followDefaults(policy, groups);
      result.chain = f.chain;
      result.chainNote = f.note;
      return result;
    }
    result.rows.push(row);
  }
  return result;
}
